import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface DateCalendarProps {
  availableDates: string[]; // Array de dateKeys no formato YYYY-MM-DD
  selectedDate: string | null;
  onSelectDate: (dateKey: string) => void;
}

export function DateCalendar({ availableDates, selectedDate, onSelectDate }: DateCalendarProps) {
  const monthNames = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ];

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  // Inicializa no mês do primeiro slot disponível
  const initialMonth = useMemo(() => {
    if (availableDates.length === 0) return new Date();
    const [year, month] = availableDates[0].split('-').map(Number);
    return new Date(year, month - 1, 1);
  }, [availableDates]);

  const [viewYear, setViewYear] = useState<number>(initialMonth.getFullYear());
  const [viewMonth, setViewMonth] = useState<number>(initialMonth.getMonth());

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
  const today = new Date();

  // Meses que têm pelo menos um slot disponível
  const monthsWithSlots = useMemo(() => {
    return new Set(availableDates.map((d) => d.slice(0, 7))); // "YYYY-MM"
  }, [availableDates]);

  const viewMonthKey = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;

  const prevMonthKey = useMemo(() => {
    const d = new Date(viewYear, viewMonth - 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, [viewYear, viewMonth]);

  const nextMonthKey = useMemo(() => {
    const d = new Date(viewYear, viewMonth + 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, [viewYear, viewMonth]);

  const canGoPrev = monthsWithSlots.has(prevMonthKey);
  const canGoNext = monthsWithSlots.has(nextMonthKey);

  const goToPrevMonth = () => {
    if (!canGoPrev) return;
    const d = new Date(viewYear, viewMonth - 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const goToNextMonth = () => {
    if (!canGoNext) return;
    const d = new Date(viewYear, viewMonth + 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const isDateAvailable = (day: number): boolean => {
    const dateKey = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return availableDates.includes(dateKey);
  };

  const isDateSelected = (day: number): boolean => {
    const dateKey = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return selectedDate === dateKey;
  };

  const isToday = (day: number): boolean => {
    return (
      today.getDate() === day &&
      today.getMonth() === viewMonth &&
      today.getFullYear() === viewYear
    );
  };

  const handleDayClick = (day: number) => {
    if (isDateAvailable(day)) {
      const dateKey = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      onSelectDate(dateKey);
    }
  };

  const renderCalendarDays = () => {
    const days: JSX.Element[] = [];

    // Empty cells before the first day
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="h-10" />);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const available = isDateAvailable(day);
      const selected = isDateSelected(day);
      const todayDate = isToday(day);

      days.push(
        <button
          key={day}
          onClick={() => handleDayClick(day)}
          disabled={!available}
          className={`
            h-10 rounded-lg font-medium text-sm transition-all duration-200
            ${
              selected
                ? 'bg-primary text-primary-foreground shadow-md scale-105'
                : available
                  ? 'bg-primary/10 text-primary hover:bg-primary/20 hover:scale-105'
                  : 'text-muted-foreground/30 cursor-not-allowed'
            }
            ${todayDate && !selected ? 'ring-2 ring-primary/30' : ''}
            ${available && !selected ? 'hover:shadow-sm' : ''}
          `}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  // Mostrar indicador de meses disponíveis além do atual
  const hasMoreMonths = canGoPrev || canGoNext;

  return (
    <div className="bg-white dark:bg-card rounded-lg p-4 border border-[#DED1BD]/50 dark:border-border">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPrevMonth}
          disabled={!canGoPrev}
          className="p-1 hover:bg-[#DED1BD]/30 dark:hover:bg-muted rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Mês anterior"
        >
          <ChevronLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <h3 className="font-semibold text-foreground">
          {monthNames[viewMonth]} {viewYear}
        </h3>
        <button
          onClick={goToNextMonth}
          disabled={!canGoNext}
          className="p-1 hover:bg-[#DED1BD]/30 dark:hover:bg-muted rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Próximo mês"
        >
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>
      </div>

      {/* Aviso quando há datas em outro mês */}
      {!monthsWithSlots.has(viewMonthKey) && hasMoreMonths && (
        <p className="text-xs text-center text-muted-foreground mb-3">
          Nenhum horário disponível neste mês
        </p>
      )}

      {/* Week days */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map((day) => (
          <div key={day} className="h-8 flex items-center justify-center">
            <span className="text-xs font-medium text-muted-foreground">{day}</span>
          </div>
        ))}
      </div>

      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-1">{renderCalendarDays()}</div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-primary/10 border border-primary/30"></div>
            <span className="text-muted-foreground">Disponível</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-primary"></div>
            <span className="text-muted-foreground">Selecionado</span>
          </div>
        </div>
      </div>
    </div>
  );
}
